// widget/src/components/VoiceInterface.tsx
import React, { useEffect, useRef } from 'react';
import type { UseOpenAIVoiceReturn } from '../hooks/useOpenAIVoice';

interface VoiceInterfaceProps {
  voice: UseOpenAIVoiceReturn;
  primaryColor: string;
  onBack: () => void;
  supabaseUrl: string;
  supabaseAnonKey: string;
  userEmail: string | null;
}

export const VoiceInterface: React.FC<VoiceInterfaceProps> = ({
  voice,
  primaryColor,
  onBack,
  supabaseUrl,
  supabaseAnonKey,
  userEmail
}) => {
  const [showThankYou, setShowThankYou] = React.useState(false);
  const startTimeRef = useRef<number | null>(null);

  // Track start time on connect
  useEffect(() => {
    if (voice.status === 'connected' && !startTimeRef.current) {
      startTimeRef.current = Date.now();
    }
  }, [voice.status]);

  // Helper for disconnect sound
  const playDisconnectSound = () => {
    try {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContext) return;

      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.connect(gain);
      gain.connect(ctx.destination);

      // Gentle "power down" tone
      osc.type = 'sine';
      osc.frequency.setValueAtTime(440, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(220, ctx.currentTime + 0.3);

      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) {
      console.error('Failed to play disconnect sound:', e);
    }
  };

  const handleEndCall = () => {
    hasEnded.current = true;

    // Calculate duration
    let durationString = 'Unknown';
    if (startTimeRef.current) {
      const seconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      durationString = `${minutes}m ${remainingSeconds}s`;
    }

    // 1. Play sound
    playDisconnectSound();

    // 2. IMMEDIATE Disconnect
    // This stops the AI from speaking immediately
    voice.disconnect();

    // 3. Show Thank You screen
    setShowThankYou(true);

    // 4. Send transcript for fail-safe processing
    // Only send if we haven't already confirmed a save via tool call
    if (voice.hasSavedLead) {
      console.log('VoiceInterface: save_lead tool already executed. Skipping failsafe transcript upload to prevent duplicate processing.');
    } else if (voice.transcript && voice.transcript.length > 50) {
      console.log('VoiceInterface: Sending transcript for analysis (Failsafe)...');
      fetch(`${supabaseUrl}/functions/v1/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({
          action: 'process_transcript',
          transcript: voice.transcript,
          userEmail: userEmail,
          duration: durationString
        })
      }).catch(err => console.error('Failed to send transcript:', err));
    }

    setTimeout(() => {
      onBack();
    }, 4000);
  };

  const {
    status,
    isConnected,
    isMicOn,
    audioLevel,
    connect,
    toggleMic,
    transcript
  } = voice;

  const hasAttemptedConnect = useRef(false);
  const hasEnded = useRef(false);

  // Auto-connect on mount - only attempt once, and don't reconnect after hanging up
  useEffect(() => {
    if (!hasAttemptedConnect.current && !hasEnded.current && status === 'idle') {
      hasAttemptedConnect.current = true;
      console.log('VoiceInterface: Auto-connecting...');
      connect();
    }
  }, [status, connect]);

  // Handle timeout status from hook
  useEffect(() => {
    if (status === 'timeout' && !hasEnded.current) {
      console.log('VoiceInterface: Detected timeout status, ending call...');
      handleEndCall();
    }
  }, [status]);

  const [hasInteraction, setHasInteraction] = React.useState(false);

  // Track interaction to fade out text
  useEffect(() => {
    if ((transcript && transcript.length > 0) || status === 'speaking') {
      setHasInteraction(true);
    }
  }, [transcript, status]);

  const getStatusText = () => {
    switch (status) {
      case 'connecting': return 'Connecting...';
      case 'connected':
        return hasInteraction ? '' : 'Go ahead, ask me anything...';
      case 'speaking': return ''; // Clean UI, orb handles it
      case 'error': return 'Connection error';
      case 'timeout': return 'Call ended due to inactivity';
      default: return 'Initializing...';
    }
  };

  // Determine animation class for the orb
  const getOrbAnimation = () => {
    if (status === 'speaking') return 'orb-speaking';
    if (status === 'connected' && isMicOn) return 'orb-listening';
    if (status === 'connecting' || status === 'idle' || status === 'timeout') return 'orb-breathing';
    return '';
  };

  const getOrbScale = () => {
    if (status === 'speaking') return 1 + (audioLevel * 0.3);
    if (status === 'connected' && isMicOn) return 1 + (audioLevel * 0.2);
    return 1;
  };

  if (showThankYou) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: '24px',
        textAlign: 'center',
        animation: 'fadeIn 0.5s ease-in'
      }}>
        <h3 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: '600' }}>Thank you for chatting!</h3>
        <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
          If you requested information, our team will follow up by the end of the next business day.
        </p>
        <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.5', marginTop: '8px' }}>
          We look forward to connecting with you soon.
        </p>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: '24px',
      textAlign: 'center'
    }}>
      {/* Animated Orb Container */}
      <div style={{
        position: 'relative',
        width: '200px',
        height: '200px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* All layers share the same absolute center */}

        {/* Outer glow rings */}
        <div
          className={getOrbAnimation()}
          style={{
            position: 'absolute',
            width: '180px',
            height: '180px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${primaryColor}25 0%, transparent 70%)`,
            zIndex: 1
          }}
        />

        <div
          className={status === 'speaking' ? 'ring-pulse' : ''}
          style={{
            position: 'absolute',
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            border: `2px solid ${primaryColor}30`,
            zIndex: 1
          }}
        />

        {/* Main orb */}
        <div
          className={status === 'speaking' ? 'main-orb-speaking' : ''}
          style={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            position: 'relative',
            zIndex: 2,
            background: status === 'error'
              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
              : `linear-gradient(135deg, ${primaryColor} 0%, ${adjustColor(primaryColor, -20)} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: `scale(${getOrbScale()})`,
            transition: 'transform 0.1s ease-out',
            boxShadow: `0 8px 32px ${primaryColor}50`,
          }}
        >
          {status === 'connecting' || status === 'idle' || status === 'timeout' ? (
            <div style={{ color: 'white' }}>
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ animation: 'spin 1s linear infinite' }}
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            </div>
          ) : (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          )}
        </div>
      </div>

      {/* Status text */}
      <div style={{
        fontSize: '16px',
        fontWeight: '500',
        color: status === 'error' ? '#dc2626' : '#333',
        marginBottom: '8px',
        minHeight: '24px', // Prevent layout jump
        transition: 'opacity 0.3s ease-in-out',
        opacity: getStatusText() ? 1 : 0
      }}>
        {getStatusText()}
      </div>

      {/* Controls - only show when connected */}
      {isConnected && (
        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
          <button
            onClick={toggleMic}
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: isMicOn ? '#f1f5f9' : '#fecaca',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s',
            }}
            aria-label={isMicOn ? 'Mute microphone' : 'Unmute microphone'}
          >
            {isMicOn ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            )}
          </button>

          <button
            onClick={handleEndCall}
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: '#fecaca',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s',
            }}
            aria-label="End conversation"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* Help text */}
      <div style={{
        marginTop: '24px',
        fontSize: '13px',
        color: '#999',
        lineHeight: '1.5',
        opacity: hasInteraction ? 0 : 1, // Also fade out help text on interaction
        transition: 'opacity 0.3s ease-in-out'
      }}>
        {status === 'connecting'
          ? "Please wait while we connect..."
          : status === 'error'
            ? "Unable to connect. Please try again."
            : "Speak naturally. I'm here to help you learn about Dr. TJ's programs."
        }
      </div>

      {/* Animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes breathing {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.9; }
        }
        @keyframes listening {
          0%, 100% { transform: scale(1); box-shadow: 0 8px 32px ${primaryColor}50; }
          50% { transform: scale(1.02); box-shadow: 0 8px 40px ${primaryColor}70; }
        }
        @keyframes speaking {
          0%, 100% { transform: scale(1); }
          25% { transform: scale(1.08); }
          50% { transform: scale(1.03); }
          75% { transform: scale(1.06); }
        }
        @keyframes mainOrbSpeaking {
          0%, 100% { transform: scale(1); }
          25% { transform: scale(1.08); }
          50% { transform: scale(1.03); }
          75% { transform: scale(1.06); }
        }
        @keyframes ringPulse {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        .orb-breathing {
          animation: breathing 2s ease-in-out infinite;
        }
        .orb-listening {
          animation: listening 1.5s ease-in-out infinite;
        }
        .orb-speaking {
          animation: speaking 0.8s ease-in-out infinite;
        }
        .main-orb-speaking {
          animation: mainOrbSpeaking 0.8s ease-in-out infinite;
        }
        .ring-pulse {
          animation: ringPulse 1.5s ease-out infinite;
        }
      `}</style>
    </div>
  );
};

// Helper to darken/lighten colors
function adjustColor(color: string, amount: number): string {
  const hex = color.replace('#', '');
  const num = parseInt(hex, 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
  return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
}

export default VoiceInterface;
